import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Sparkles, Activity, TrendingUp, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { aiHealthScore, aiSuccessPredictor } from "@/lib/ai";
import { fetchCampaigns } from "@/lib/api";

// ---------- reusable UI ----------

const SEVERITY_COLOR = (score) => {
  if (score >= 90) return { text: "text-forest-700", ring: "stroke-forest-600", fill: "bg-forest-500" };
  if (score >= 70) return { text: "text-saffron-700", ring: "stroke-saffron-500", fill: "bg-saffron-500" };
  return { text: "text-red-600", ring: "stroke-red-500", fill: "bg-red-500" };
};

const CircularScore = ({ value, label = "Overall", size = 200, testid }) => {
  const clamp = Math.max(0, Math.min(100, value || 0));
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (clamp / 100);
  const tone = SEVERITY_COLOR(clamp);
  return (
    <div className="relative inline-flex items-center justify-center" data-testid={testid}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} strokeWidth={stroke} className="stroke-warm-100" fill="none" />
        <circle
          cx={size/2} cy={size/2} r={r} strokeWidth={stroke}
          fill="none" strokeLinecap="round"
          className={tone.ring}
          strokeDasharray={`${dash} ${c - dash}`}
          style={{ transition: "stroke-dasharray 1s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className={`font-display font-bold text-5xl tracking-tight ${tone.text}`}>{clamp}</div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy-500 mt-1">{label}</div>
      </div>
    </div>
  );
};

const ScoreBar = ({ label, score, testid }) => {
  const tone = SEVERITY_COLOR(score);
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-sm">
        <span className="font-semibold text-navy-800">{label}</span>
        <span className={`font-display font-bold ${tone.text}`}>{score}</span>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-warm-100 overflow-hidden">
        <div className={`h-full ${tone.fill} transition-all duration-700`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
};

const PRIORITY_STYLE = {
  high:   "bg-red-50 text-red-700 border-red-200",
  medium: "bg-saffron-500/10 text-saffron-700 border-saffron-500/30",
  low:    "bg-cream-100 text-navy-700 border-warm-200",
};

// ---------- Campaign-picker + panels ----------

const CampaignPicker = ({ campaigns, selectedId, onSelect }) => (
  <div className="rounded-2xl border border-warm-200 bg-white p-5">
    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy-500 mb-2">Pick a campaign to analyze</div>
    <select
      value={selectedId || ""}
      onChange={(e) => onSelect(e.target.value)}
      data-testid="campaign-check-select"
      className="w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition"
    >
      <option value="">— select a campaign —</option>
      {campaigns.map((c) => (
        <option key={c.id} value={c.id}>{c.title || "(untitled)"}</option>
      ))}
    </select>
    <p className="mt-2 text-xs text-navy-500">Analyzing evaluates your title, story, media, rewards, goal, and creator profile.</p>
  </div>
);

const HealthPanel = ({ data }) => (
  <div className="rounded-2xl border border-warm-200 bg-white p-6 space-y-6" data-testid="health-panel">
    <div className="flex items-center gap-6 flex-wrap">
      <CircularScore value={data.overall_score} testid="health-overall-score" />
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center gap-2 text-saffron-700 text-[11px] font-semibold uppercase tracking-[0.2em]">
          <Activity className="h-3.5 w-3.5" /> Grade · {data.grade}
        </div>
        <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-navy-800">{data.one_line_verdict}</h3>
        {data.strengths?.length > 0 && (
          <ul className="mt-3 space-y-1">
            {data.strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-navy-700"><CheckCircle2 className="h-4 w-4 text-forest-600 mt-0.5 flex-shrink-0" /> {s}</li>
            ))}
          </ul>
        )}
      </div>
    </div>

    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy-500 mb-3">Category scores</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {Object.entries(data.category_scores || {}).map(([k, v]) => (
          <ScoreBar key={k} label={k.replace(/_/g, " ")} score={v} testid={`health-cat-${k}`} />
        ))}
      </div>
    </div>

    {data.improvements?.length > 0 && (
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy-500 mb-3">What to fix (in order)</div>
        <div className="space-y-2">
          {data.improvements.map((imp, i) => (
            <div key={i} data-testid={`health-imp-${i}`} className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 text-sm ${PRIORITY_STYLE[imp.priority] || PRIORITY_STYLE.low}`}>
              <span className="uppercase text-[10px] font-bold tracking-wider mt-0.5 flex-shrink-0">{imp.priority}</span>
              <div className="flex-1">
                <span>{imp.text}</span>
                {imp.category && <span className="ml-1.5 text-[10px] uppercase tracking-wider opacity-70">· {imp.category}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const PredictorPanel = ({ data }) => (
  <div className="rounded-2xl border border-warm-200 bg-white p-6 space-y-6" data-testid="predictor-panel">
    <div className="flex items-center gap-6 flex-wrap">
      <CircularScore value={data.probability} label="Chance" testid="predictor-probability" />
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center gap-2 text-saffron-700 text-[11px] font-semibold uppercase tracking-[0.2em]">
          <TrendingUp className="h-3.5 w-3.5" /> Confidence · {data.confidence}
        </div>
        <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-navy-800">{data.verdict_line}</h3>
        {data.projected_range && (
          <div className="mt-2 text-sm text-navy-600">
            Projected range: <span className="font-semibold text-navy-800">{data.projected_range.low_pct}% – {data.projected_range.high_pct}%</span>
          </div>
        )}
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-forest-700 mb-2">Working in your favor</div>
        <ul className="space-y-1.5">
          {(data.reasons_positive || []).map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-navy-700"><CheckCircle2 className="h-4 w-4 text-forest-600 mt-0.5 flex-shrink-0" /> {r}</li>
          ))}
        </ul>
      </div>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-red-600 mb-2">Risks to address</div>
        <ul className="space-y-1.5">
          {(data.reasons_negative || []).map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-navy-700"><AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" /> {r}</li>
          ))}
        </ul>
      </div>
    </div>

    {data.recommendations?.length > 0 && (
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy-500 mb-3">Highest-impact fixes</div>
        <div className="space-y-2">
          {data.recommendations.map((r, i) => (
            <div key={i} data-testid={`predictor-rec-${i}`} className="flex items-start gap-3 rounded-xl border border-warm-200 bg-cream-100 px-3 py-2.5 text-sm text-navy-800">
              <span className="uppercase text-[10px] font-bold tracking-wider mt-0.5 flex-shrink-0 text-saffron-700">{r.impact}</span>
              <span>{r.text}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// ---------- Page ----------

const PanelLoading = ({ label }) => (
  <div className="rounded-2xl border border-warm-200 bg-white p-10 text-center">
    <Loader2 className="h-7 w-7 mx-auto animate-spin text-saffron-600" />
    <p className="mt-3 text-sm font-semibold text-navy-800">{label}</p>
  </div>
);

const PanelEmpty = ({ label }) => (
  <div className="rounded-2xl border border-warm-200 bg-cream-100 p-8 text-center text-sm text-navy-500">
    {label}
  </div>
);

const CampaignCheck = () => {
  const { user, loading: authLoading, requireAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeTab = params.get("tab") === "predictor" ? "predictor" : "health";
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [health, setHealth] = useState(null);
  const [predict, setPredict] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) requireAuth("Sign in to run an AI campaign check.");
  }, [user, authLoading, requireAuth]);

  useEffect(() => {
    if (!user) return;
    fetchCampaigns({ mine: true, limit: 25 }).then((r) => {
      const items = r.items || r || [];
      setCampaigns(items);
      if (items.length && !selectedId) setSelectedId(items[0].id);
    }).catch(() => { /* no campaigns yet */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const selected = campaigns.find((c) => c.id === selectedId);

  const buildSnapshot = useCallback((c) => ({
    title: c?.title || "",
    tagline: c?.tagline || "",
    story: c?.story || "",
    cover: c?.cover || "",
    gallery: c?.gallery || [],
    video_url: c?.video_url || "",
    goal: c?.goal || 0,
    rewards: c?.rewards || [],
    days: c?.days_left || c?.days || 30,
    faqs: c?.faqs || [],
    creator_bio: c?.creator?.bio || "",
    creator_avatar: c?.creator?.avatar_url || "",
    category: c?.category || "",
  }), []);

  const runAll = useCallback(async () => {
    if (!selected) { toast.error("Pick a campaign first"); return; }
    const snap = buildSnapshot(selected);
    setSnapshot(snap);
    setLoading(true);
    setHealth(null); setPredict(null);
    const [h, p] = await Promise.allSettled([
      aiHealthScore(snap),
      aiSuccessPredictor(snap),
    ]);
    if (h.status === "fulfilled") setHealth(h.value.data); else toast.error(h.reason?.message || "Health analysis failed");
    if (p.status === "fulfilled") setPredict(p.value.data); else toast.error(p.reason?.message || "Predictor failed");
    setLoading(false);
  }, [selected, buildSnapshot]);

  const setTab = (t) => setParams({ tab: t }, { replace: true });

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10" data-testid="campaign-check-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-saffron-700">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Campaign Check</span>
          </div>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-bold tracking-tight text-navy-800">Grade your campaign before you go live.</h1>
          <p className="mt-2 text-navy-600 max-w-2xl">Two AI passes: an auditor for what's missing, and a predictor for likely outcome. Same input, different lens.</p>
        </div>
        <button
          onClick={runAll}
          disabled={loading || !selectedId}
          data-testid="campaign-check-run"
          className="btn-saffron inline-flex items-center gap-2 rounded-2xl h-11 px-5 font-semibold disabled:opacity-70"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? "Analyzing…" : "Run analysis"}
        </button>
      </div>

      <div className="mt-6">
        <CampaignPicker campaigns={campaigns} selectedId={selectedId} onSelect={setSelectedId} />
        {campaigns.length === 0 && (
          <div className="mt-4 rounded-2xl border border-warm-200 bg-white p-6 text-sm text-navy-600">
            You don't have any drafts yet. <a href="/start" className="text-saffron-700 font-semibold inline-flex items-center gap-1">Create one <ArrowRight className="h-3 w-3" /></a>
          </div>
        )}
      </div>

      {(health || predict || loading) && (
        <>
          <div className="mt-8 inline-flex rounded-2xl border border-warm-200 bg-white p-1" role="tablist">
            <button
              role="tab"
              onClick={() => setTab("health")}
              data-testid="tab-health"
              className={`px-4 h-10 rounded-xl text-sm font-semibold transition-colors ${activeTab === "health" ? "bg-navy-800 text-white" : "text-navy-600 hover:text-navy-800"}`}
            >
              <Activity className="inline h-4 w-4 mr-1.5" /> Health Score
            </button>
            <button
              role="tab"
              onClick={() => setTab("predictor")}
              data-testid="tab-predictor"
              className={`px-4 h-10 rounded-xl text-sm font-semibold transition-colors ${activeTab === "predictor" ? "bg-navy-800 text-white" : "text-navy-600 hover:text-navy-800"}`}
            >
              <TrendingUp className="inline h-4 w-4 mr-1.5" /> Success Predictor
            </button>
          </div>

          <div className="mt-4">
            {activeTab === "health"    && health   && <HealthPanel data={health} />}
            {activeTab === "predictor" && predict  && <PredictorPanel data={predict} />}
            {activeTab === "health"    && !health   && loading && <PanelLoading label="Auditing your campaign…" />}
            {activeTab === "predictor" && !predict  && loading && <PanelLoading label="Estimating success probability…" />}
            {activeTab === "health"    && !health   && !loading && <PanelEmpty label="Run analysis to see the health score." />}
            {activeTab === "predictor" && !predict  && !loading && <PanelEmpty label="Run analysis to see the success prediction." />}
          </div>
        </>
      )}

      {!loading && !health && !predict && (
        <div className="mt-8 rounded-2xl border border-warm-200 bg-cream-100 p-8 text-center text-navy-600 text-sm">
          Pick a campaign above and click <span className="font-semibold text-saffron-700">Run analysis</span> to grade it and estimate success.
        </div>
      )}
    </div>
  );
};

export default CampaignCheck;
